"use client";

import BottomCta from "@/components/BottomCta";
import { motion } from "framer-motion";
import { HeartPulse, ShieldCheck, Users, Briefcase, Zap, Phone, Check, CheckCircle2 } from "lucide-react";

const PLANS = [
  { icon: ShieldCheck, title: "Regulatory Coverage", desc: "UAE labour compliant insurance plans that meet all legal requirements." },
  { icon: Users, title: "Employee Wellbeing", desc: "Individual and family coverage options tailored to your team." },
  { icon: Briefcase, title: "Corporate Plans", desc: "Premium business insurance solutions for companies of all sizes." },
  { icon: Zap, title: "Fast Activation", desc: "Insurance setup completed within 48 hours of approval." },
];

const STEPS = [
  { num: "01", title: "Understanding Your Needs", desc: "We review your team size, requirements, and compliance obligations." },
  { num: "02", title: "Plan Selection", desc: "We recommend the right plan from top UAE insurers based on your needs." },
  { num: "03", title: "Documentation", desc: "We guide you through preparing and submitting all required documents." },
  { num: "04", title: "Activation", desc: "Coverage is confirmed and activated, fully compliant with UAE labour laws." },
];

const WHY_CHOOSE = [
  "Complete coverage with medical and dental benefits",
  "Fast onboarding and activation within 48 hours",
  "Plans from trusted top UAE insurance providers",
  "UAE labour law compliant for all employees",
  "Ongoing support and renewal management",
];

export default function HealthInsurancePage() {
  return (
    <main className="bg-[#FAFAFA] min-h-screen w-full overflow-x-hidden text-[#000000]">

      {/* HERO */}
      <section className="relative min-h-[85vh] flex items-center justify-center py-20 lg:py-32 2xl:py-48 overflow-hidden">
        <div className="max-w-4xl xl:max-w-6xl 2xl:max-w-7xl mx-auto w-full px-6 text-center flex flex-col items-center relative z-10">

          <motion.div
            initial={{ opacity: 0, y: -15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-[#B8893A]/20 bg-[#B8893A]/5 mb-8"
          >
            <span className="w-2 h-2 rounded-full bg-[#B8893A]" />
            <span className="text-[10px] sm:text-xs font-bold text-[#B8893A] uppercase tracking-[0.2em]">
              UAE Labour Compliant · Fast Activation · Top Insurers
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="text-4xl sm:text-5xl md:text-6xl xl:text-7xl 2xl:text-8xl font-bold tracking-tight leading-[1.15] mb-6 max-w-5xl text-[#000000]"
          >
            Health Insurance <br />
            <span className="font-serif font-normal text-[#B8893A]">for UAE Teams That Thrive</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="text-gray-500 text-base sm:text-lg md:text-xl xl:text-2xl max-w-2xl xl:max-w-4xl font-light leading-relaxed mb-10"
          >
            Comprehensive health insurance plans tailored to UAE labour and residence requirements, for employees, families, and growing teams.
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto"
          >
            <a
              href="https://wa.me/971507987470"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex w-full sm:w-auto items-center justify-center px-10 py-4 xl:px-14 xl:py-5 font-bold text-white bg-[#B8893A] rounded-xl transition-all duration-300 hover:bg-[#a3762f] hover:scale-[1.03] active:scale-[0.98] text-base xl:text-lg tracking-wide"
            >
              <Phone size={18} className="mr-2" />
              Talk to Our Team
            </a>
          </motion.div>
        </div>
      </section>

      {/* WHO WE SERVE & APPROACH */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 bg-white border-y border-gray-100">
        <div className="max-w-[1600px] mx-auto grid md:grid-cols-2 gap-12 xl:gap-24">
          <div className="flex flex-col justify-center">
            <span className="text-xs font-bold text-[#B8893A] uppercase tracking-widest mb-2">Who We Cover</span>
            <h2 className="text-2xl sm:text-3xl xl:text-4xl font-black mb-4">Built for every team size and structure</h2>
            <p className="text-gray-500 font-light leading-relaxed xl:text-lg">
              Whether you are a startup onboarding your first employee or an enterprise managing hundreds, we have the right plan to keep your team covered and compliant.
            </p>
          </div>
          <div className="flex flex-col justify-center border-t md:border-t-0 md:border-l border-gray-100 pt-8 md:pt-0 md:pl-12 xl:pl-24">
            <span className="text-xs font-bold text-[#B8893A] uppercase tracking-widest mb-2">Our Approach</span>
            <h2 className="text-2xl sm:text-3xl xl:text-4xl font-black mb-4">Simple setup, reliable coverage</h2>
            <p className="text-gray-500 font-light leading-relaxed xl:text-lg">
              We handle everything from plan selection to documentation and activation, so your team is protected without the administrative burden falling on you.
            </p>
          </div>
        </div>
      </section>

      {/* PLANS */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 xl:py-32">
        <div className="max-w-[1600px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl xl:text-5xl font-black tracking-tight mb-4">Plans for Every Team Size</h2>
            <p className="text-gray-400 font-medium xl:text-lg uppercase tracking-wider text-xs">From solo founders to enterprise workforces</p>
            <div className="w-12 h-[3px] bg-[#B8893A] mx-auto mt-4 rounded-full" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 xl:gap-8">
            {PLANS.map((item, i) => {
              const IconComponent = item.icon;
              return (
                <div
                  key={i}
                  className="group bg-white p-8 rounded-2xl border border-gray-100/80 transition-all duration-300 hover:shadow-md flex flex-col justify-between"
                >
                  <div>
                    <div className="w-10 h-10 rounded-xl bg-[#B8893A]/5 flex items-center justify-center text-[#B8893A] mb-6 group-hover:bg-[#B8893A] group-hover:text-white transition-all duration-300">
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <h3 className="text-xl font-bold mb-2 group-hover:text-[#B8893A] transition-colors duration-300">{item.title}</h3>
                    <p className="text-gray-500 font-light text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* OUR PROCESS */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 xl:py-32 bg-white border-t border-gray-100">
        <div className="max-w-[1600px] mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-3xl sm:text-4xl xl:text-5xl font-black tracking-tight mb-4">Our Process</h2>
            <p className="text-gray-400 font-medium xl:text-lg uppercase tracking-wider text-xs">Structured. Efficient. Reliable.</p>
            <div className="w-12 h-[3px] bg-[#B8893A] mx-auto mt-4 rounded-full" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8">
            {STEPS.map((step, i) => (
              <div key={i} className="relative group bg-white p-8 rounded-2xl border border-gray-100/80 shadow-sm hover:shadow-xl transition-all duration-300">
                <div className="text-6xl xl:text-7xl font-black text-gray-100 group-hover:text-[#B8893A]/10 transition-colors duration-300 mb-4">
                  {step.num}
                </div>
                <h3 className="text-lg font-bold mb-2 tracking-tight">{step.title}</h3>
                <p className="text-gray-500 font-light text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 bg-[#FAFAFA] border-t border-gray-100">
        <div className="max-w-[1600px] mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <span className="text-xs font-bold text-[#B8893A] uppercase tracking-widest mb-2">Why Choose Us</span>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">Because protecting your team matters</h2>
            <p className="text-gray-500 font-light leading-relaxed">
              We don't just provide insurance. We make sure your team is protected correctly, your business stays compliant, and the process is as straightforward as possible.
            </p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {WHY_CHOOSE.map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-4 border border-gray-100 bg-white rounded-xl">
                <Check className="w-4 h-4 text-[#B8893A] shrink-0 mt-0.5" />
                <span className="text-sm font-semibold text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BottomCta
        title="Get insured in days, not weeks"
        subtitle="Full support from plan selection to approval, documentation and activation, handled by experts who understand UAE requirements."
      />
    </main>
  );
}
